import { IGetListYearGroupService } from "../../../../abstractions/services/actions/IGetListYearGroupService";
import { serviceProvider } from "../../../../comun/provider/ServiceProvider";
import { TareaEjecucionResponse } from "../../../../modulos/rastro/response/TareaEjecucionResponse";
import { TareaEjecucionDeleteGrpcService } from "../../../../modulos/rastro/services/TareaEjecucionDeleteGrpcService";
import {
  DiaTareaEjecucionesBucket,
  fx_createTareaEjecucionGetYearGroupListByFincaIdService,
} from "../../../../modulos/rastro/services/TareaEjecucionGetYearGroupListByFincaIdService";
import { formatFechaCorta, MESES } from "../../../../utils/fechaUtils";
import { BaseSecondSidebarItemRender } from "../../../base/sidebar/BaseSecondSidebarItemRender";
import { BaseSecondSidebarRender } from "../../../base/sidebar/BaseSecondSidebarRender";
import { ConfirmModalRender } from "../../../implementations/modal/ConfirmModalRender";
import { TareaEjecucionEditModal } from "../modal/TareaEjecucionEditModal";
import { TareaEjecucionFullScreenModalRender } from "../modal/TareaEjecucionFullScreenModalRender";
import { TareaEjecucionSidebarItemRender } from "../list/TareaEjecucionSidebarItemRender";

/** Contador para ids únicos por instancia (select de mes). */
let TAREA_SIDEBAR_ID = 0;

/** Segundo sidebar del módulo de Tareas (rastro): ejecuciones de tarea de la
 *  finca activa agrupadas por año → mes → día. Cada item es un día; su
 *  contenido expandido son las ejecuciones de ese día. */
export class TareaEjecucionSecondSidebarRender extends BaseSecondSidebarRender<DiaTareaEjecucionesBucket> {
  /** Id único de la instancia (para el id del select de mes). */
  private readonly _tareaSidebarId: number;
  /** Mes elegido (0–11). `null` solo antes de la primera carga: `filterItems`
   *  siempre lo resuelve a un mes real (el más reciente disponible) antes de
   *  filtrar — el mes es obligatorio, no hay opción "todos". */
  private _mes: number | null = null;
  private _monthSelect: HTMLSelectElement | null = null;

  /**
   * @param onBack             Acción del botón volver.
   * @param _onDiaSelected     Notifica el día seleccionado (el mapa dibuja
   *                           todas sus ejecuciones).
   * @param _onToggleEjecucion Notifica que se prendió/apagó una ejecución
   *                           puntual del día expandido.
   */
  constructor(
    onBack: () => void,
    private readonly _onDiaSelected: (dia: DiaTareaEjecucionesBucket) => void,
    private readonly _onToggleEjecucion: (
      ejecucionId: string,
      visible: boolean,
    ) => void,
  ) {
    super(onBack);
    TAREA_SIDEBAR_ID += 1;
    this._tareaSidebarId = TAREA_SIDEBAR_ID;
  }

  protected get title(): string {
    return "TAREAS";
  }

  /** Ejecuciones de la finca activa, agrupadas por año → día. */
  protected getYearGroupListService(): IGetListYearGroupService<DiaTareaEjecucionesBucket> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    return fx_createTareaEjecucionGetYearGroupListByFincaIdService(finca.id);
  }

  /** Select de mes: arranca vacío, `filterItems` lo puebla con los meses que
   *  el año elegido realmente tiene. */
  protected renderSelectors(): string {
    return `<select class="sx-select" id="tes-mes-${this._tareaSidebarId}"></select>`;
  }

  /** Filtra los días del año al mes elegido. El mes es obligatorio: si no hay
   *  uno elegido, o el elegido no existe en este año, cae al más reciente
   *  disponible. Repuebla el select con solo los meses que el año tiene. */
  protected filterItems(
    items: DiaTareaEjecucionesBucket[],
  ): DiaTareaEjecucionesBucket[] {
    const mesesDisponibles = [
      ...new Set(items.map((dia) => this._mesDe(dia))),
    ].sort((a, b) => b - a);

    if (this._mes == null || !mesesDisponibles.includes(this._mes)) {
      this._mes = mesesDisponibles[0] ?? null;
    }
    this._populateMonthSelect(mesesDisponibles);

    return items.filter((dia) => this._mesDe(dia) === this._mes);
  }

  /** Mes (0–11) de un balde de día, según su `fecha`. */
  private _mesDe(dia: DiaTareaEjecucionesBucket): number {
    return Number(dia.fecha.split("-")[1]) - 1;
  }

  /** Puebla el select con los meses disponibles y marca el elegido. */
  private _populateMonthSelect(meses: number[]): void {
    if (!this._monthSelect) return;
    this._monthSelect.innerHTML = meses
      .map((mes) => `<option value="${mes}">${MESES[mes]}</option>`)
      .join("");
    if (this._mes != null) this._monthSelect.value = String(this._mes);
  }

  /** Item de día (sus ejecuciones van en el contenido expandido). */
  protected createItem(
    item: DiaTareaEjecucionesBucket,
  ): BaseSecondSidebarItemRender {
    return new TareaEjecucionSidebarItemRender(
      item,
      this._onDiaSelected,
      this._onToggleEjecucion,
      (ejecucion) => this._openEdit(ejecucion),
      (ejecucion) => this._confirmDelete(ejecucion),
      (ejecucion) => this._openFullScreen(item, ejecucion),
    );
  }

  /** Abre el modal a pantalla completa con el mapa de esa sola ejecución. Le
   *  pasa además el día al que pertenece, que es el que fija los colores. */
  private _openFullScreen(
    dia: DiaTareaEjecucionesBucket,
    ejecucion: TareaEjecucionResponse,
  ): void {
    new TareaEjecucionFullScreenModalRender(dia, ejecucion).init();
  }

  /** Abre el modal de edición (asignar tarea + detalle de productos); el
   *  modal full-screen ya recarga la página al guardar. */
  private _openEdit(ejecucion: TareaEjecucionResponse): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    new TareaEjecucionEditModal(ejecucion, finca).init();
  }

  /** Confirma y elimina la ejecución; al éxito recarga la página. */
  private _confirmDelete(ejecucion: TareaEjecucionResponse): void {
    new ConfirmModalRender({
      title: "Eliminar ejecución",
      message: `¿Está seguro que desea eliminar la ejecución del GPS Tracker #${ejecucion.gpsTrackerNumero} del <strong>${formatFechaCorta(ejecucion.fechaInicio)}</strong>? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        await new TareaEjecucionDeleteGrpcService().deleteAsync(ejecucion);
        serviceProvider.getLayoutService().reload();
      },
    }).init();
  }

  /** Cablea, además de lo de la base, el cambio del select de mes. */
  override connect(): void {
    super.connect();
    this._monthSelect = document.getElementById(
      `tes-mes-${this._tareaSidebarId}`,
    ) as HTMLSelectElement | null;
    this._monthSelect?.addEventListener("change", this._onMonthChange);
  }

  /** Libera el listener del select de mes además de lo de la base. */
  override dispose(): void {
    this._monthSelect?.removeEventListener("change", this._onMonthChange);
    super.dispose();
  }

  private _onMonthChange = (): void => {
    if (!this._monthSelect) return;
    this._mes = Number(this._monthSelect.value);
    this.refreshCurrentYear();
  };
}
